// 旧2ファイル形式（{castId}_btl.glb / {castId}_move.glb）の敵GLBを、プロジェクトを開いた
// 時点で統合1ファイル形式（{castId}.glb・idle/walk 2クリップ）へ移行する IndexedDB 実行器。
// 判定・プラン・JSON書き換えは lib/glb-migrate-core.mjs（純関数）、アニメ統合は
// lib/glb-anim-merge.mjs（生成パイプラインと同一実装）を使う。
//
// 方針（2026-08-08 指示）:
// - バイト同一 or アニメ無しの複製ペア（リグ無し敵）は統合せず新名へコピーだけ
// - アニメ付きペアは idle=btl / walk=move の2クリップへ統合
// - 成功した base のみ旧ファイルを削除。失敗ペアは現状維持でログに残す（プロジェクトを壊さない）
import { listAssetKeysByPrefix, getAssetFile, applyAssetChanges } from './idb.mjs';
import { classifyPair, planEnemyGlbMigration, enemyGlbKeyPrefix } from '../lib/glb-migrate-core.mjs';
import { mergeEnemyAnimationGlb } from '../lib/glb-anim-merge.mjs';
import { auditLog } from '../lib/logger.mjs';

function toU8(data) {
    if (data instanceof Uint8Array) return data;
    if (data instanceof ArrayBuffer) return new Uint8Array(data);
    if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    return null;
}

/**
 * 対象ゲームの敵GLBを統合形式へ移行する。旧形式が無ければ即 no-op（キー走査のみ）。
 * @returns {Promise<{ migratedBases: string[], failed: Array<{ base: string, reason: string }> }>}
 */
export async function migrateEnemyGlbAssets({ projectId, gameId, onLog = () => {} }) {
    const result = { migratedBases: [], failed: [] };
    if (!gameId) return result;
    const keys = await listAssetKeysByPrefix(enemyGlbKeyPrefix(gameId));
    const plan = planEnemyGlbMigration(keys);
    if (plan.length === 0) return result;

    const puts = [];
    const deletes = [];
    for (const item of plan) {
        try {
            if (item.action === 'unify') {
                const btl = toU8(await getAssetFile(item.btl));
                const move = toU8(await getAssetFile(item.move));
                if (!btl || !move) throw new Error('旧GLBの読み出しに失敗');
                if (classifyPair(btl, move) === 'copy') {
                    // リグ無しの複製ペア（アニメ無し）→ 統合処理を通さずそのまま新名へ
                    puts.push({ path: item.out, data: btl });
                    onLog(`  ✔ ${item.base}: アニメ無し複製ペアをコピーで移行`);
                } else {
                    const merged = await mergeEnemyAnimationGlb(btl, move, { onLog });
                    puts.push({ path: item.out, data: merged.bytes });
                    onLog(`  ✔ ${item.base}: アニメ2本を統合（walk=${merged.walkMode === 'static' ? '静止代替' : 'リターゲット'}）`);
                }
            } else if (item.action === 'rename') {
                const src = toU8(await getAssetFile(item.src));
                if (!src) throw new Error('旧GLBの読み出しに失敗');
                puts.push({ path: item.out, data: src });
                onLog(`  ✔ ${item.base}: 片割れファイルを新名へ改名`);
            } else {
                // cleanup: 統合ファイルが既に存在（再生成済み等）→ 旧ペアの削除だけ
                onLog(`  ✔ ${item.base}: 統合済みのため旧ファイルのみ削除`);
            }
            deletes.push(...item.deletes);
            result.migratedBases.push(item.base);
        } catch (error) {
            // 失敗した base は旧ファイルを残し、参照書き換えもしない（現状のまま動き続ける）
            result.failed.push({ base: item.base, reason: String(error?.message ?? error) });
            onLog(`  ⚠ ${item.base}: 移行に失敗したため旧形式のまま残します（${String(error?.message ?? error)}）`);
        }
    }

    if (puts.length > 0 || deletes.length > 0) {
        await applyAssetChanges(projectId, puts, deletes);
    }
    auditLog('story-enemy-glb-migrate', {
        projectId,
        gameId,
        migrated: result.migratedBases,
        failed: result.failed,
    });
    return result;
}
